// IndexedDB schema for the offline outbox (spec §8.1).
//
// The outbox holds committed-but-unsynced sales — this is MONEY OWED, so the
// store is APPEND-ONLY across schema versions: the `upgrade` only ever ADDS
// stores/indexes; it must NEVER drop or re-create `outbox` (that would wipe
// un-drained money). A static test guards the migration shape.

import { type DBSchema, type IDBPDatabase, openDB } from "idb";

export type OutboxStatus = "pending" | "syncing" | "synced" | "conflict";

/** The commit item the device priced offline — mirrors the /transactions/sync
 *  per-item contract (decimals as strings, §3). */
export interface CommitPayload {
  tx_uuid: string;
  from_city_id: number;
  to_city_id: number;
  total_mass_tn: string;
  front_axle_tn: string;
  total_axles: number;
  is_oversized: boolean;
  requires_permit?: boolean;
  vehicle_plate: string;
  owner_name?: string;
  // Distance the device priced against; the server reconciles vs the live matrix.
  submitted_distance_km: string;
  // Device-clock business timestamp (ISO) — drives invoice year + EOS bucketing.
  captured_at: string;
  offline_captured: true;
  // The total the device charged; the server re-derives and rejects a mismatch.
  client_total_tenne: number;
}

export interface OutboxItem {
  tx_uuid: string;
  payload: CommitPayload;
  captured_at: string; // FIFO drain key (indexed)
  status: OutboxStatus;
  invoice_number?: string | undefined; // official number, back-propagated at drain
  reconciliation_flag?: string | undefined;
  reason?: string | undefined; // set when status === "conflict"
  enqueued_at: string;
}

/** Cached reference data so the calculator works offline (§11). */
export interface ProvisioningBlob {
  version: string; // active_tariff_version (staleness key)
  dataVersion: string; // cities + distance-matrix content hash (staleness key)
  engineVersion: string;
  wasmHash: string | null;
  snapshotJson: string; // verbatim snapshot_to_jsonable (engine input)
  distances: Array<[number, number, string]>; // [city_low_id, city_high_id, distance@2dp]
  cities: unknown[];
  homeCityId: number | null;
  cachedAt: string;
}

interface OperatorDB extends DBSchema {
  outbox: {
    key: string; // tx_uuid
    value: OutboxItem;
    indexes: { by_captured_at: string; by_status: OutboxStatus };
  };
  provisioning: {
    key: string; // PROVISIONING_KEY
    value: ProvisioningBlob & { id: string };
  };
}

export const DB_NAME = "awtoban-operator";
export const DB_VERSION = 2;
export const PROVISIONING_KEY = "active";

// Single cached connection per process (one open handle, not one-per-call).
let dbPromise: Promise<IDBPDatabase<OperatorDB>> | null = null;

export function openOperatorDB(): Promise<IDBPDatabase<OperatorDB>> {
  if (dbPromise === null) {
    dbPromise = openDB<OperatorDB>(DB_NAME, DB_VERSION, {
      // APPEND-ONLY (§8.1): only ever ADD stores; never drop/recreate `outbox`
      // (that would wipe un-drained money).
      upgrade(db) {
        if (!db.objectStoreNames.contains("outbox")) {
          const store = db.createObjectStore("outbox", { keyPath: "tx_uuid" });
          store.createIndex("by_captured_at", "captured_at");
          store.createIndex("by_status", "status");
        }
        if (!db.objectStoreNames.contains("provisioning")) {
          db.createObjectStore("provisioning", { keyPath: "id" });
        }
      },
    });
  }
  return dbPromise;
}

/** Test helper: wipe the outbox between tests (the connection is reused). */
export async function _clearForTests(): Promise<void> {
  const db = await openOperatorDB();
  await db.clear("outbox");
}
